import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { SETTINGS } from "@/constants/testIds";
import { toast } from "sonner";
import { ChevronDown, Plus, Pencil, Trash2, Upload, Search, Cloud, Calendar as CalendarIcon, Download } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useBranding } from "@/contexts/BrandingContext";

const TABS = [
  { id: "fiscal", label: "Fiscal Year", testId: SETTINGS.tabFiscal },
  { id: "timezone", label: "Timezone", testId: SETTINGS.tabTimezone },
  { id: "users", label: "Users", testId: SETTINGS.tabUsers },
  { id: "access", label: "Access Requests", testId: "settings-tab-access" },
  { id: "security", label: "Security", testId: "settings-tab-security" },
  { id: "branding", label: "Branding", testId: "settings-tab-branding" },
  { id: "perspectives", label: "Perspectives & Rating", testId: SETTINGS.tabPerspectives },
  { id: "integrations", label: "Integrations", testId: SETTINGS.tabIntegrations },
  { id: "email", label: "Email Routing", testId: SETTINGS.tabEmailRouting },
  { id: "rating", label: "Rating Adjustment", testId: SETTINGS.tabRating },
  { id: "increase", label: "Increase Config", testId: "settings-tab-increase" },
  { id: "schedule", label: "Quarter Scheduler", testId: "settings-tab-schedule" },
];

const TIMEZONES = [
  "Africa/Johannesburg", "Africa/Cairo", "Europe/London", "Europe/Berlin",
  "America/New_York", "America/Los_Angeles", "Asia/Dubai", "Asia/Kolkata",
  "Asia/Singapore", "Australia/Sydney", "UTC",
];

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function fyOptions() {
  const cur = new Date().getFullYear();
  const out = [];
  for (let y = cur - 2; y <= cur + 10; y++) out.push(`FY${y}`);
  return out;
}

export default function Settings() {
  const [tab, setTab] = useState("fiscal");
  const [s, setS] = useState(null);
  const { refresh: refreshBranding } = useBranding();

  useEffect(() => { (async () => {
    const { data } = await api.get("/settings");
    setS(data);
  })(); }, []);

  if (!s) return <div className="text-sm text-zinc-500">Loading…</div>;

  const set = (path, value) => {
    const next = structuredClone(s);
    const keys = path.split(".");
    let cur = next;
    for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
    cur[keys[keys.length - 1]] = value;
    setS(next);
  };

  const save = async () => {
    try {
      const { data } = await api.put("/settings", s);
      setS(data);
      // Refresh header/tab/login branding immediately after save.
      await refreshBranding();
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Settings</div>
          <h1 className="text-4xl font-bold text-zinc-900 mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Organization configuration
          </h1>
        </div>
        {tab !== "users" && tab !== "access" && tab !== "security" && (
          <button onClick={save} data-testid={SETTINGS.saveButton}
            style={{ background: "var(--brand-primary)" }}
            className="text-white px-5 py-2.5 rounded-sm text-sm font-medium hover:opacity-90">
            Save changes
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
        <aside>
          <ul className="border border-zinc-200 bg-white">
            {TABS.map((t) => (
              <li key={t.id}>
                <button data-testid={t.testId} onClick={() => setTab(t.id)}
                  style={tab === t.id ? { background: "var(--brand-primary)", color: "white" } : undefined}
                  className={`w-full text-left px-4 py-3 text-sm border-b border-zinc-200 last:border-b-0 ${
                    tab === t.id ? "" : "hover:bg-zinc-50 text-zinc-700"
                  }`}>
                  {t.label}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <section className="border border-zinc-200 bg-white p-8 min-h-[400px]">
          {tab === "fiscal" && <FiscalSection s={s} set={set} />}
          {tab === "timezone" && (
            <div className="max-w-md">
              <Field label="Default timezone">
                <select className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white" value={s.timezone} onChange={(e) => set("timezone", e.target.value)}>
                  {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                </select>
              </Field>
              <p className="text-xs text-zinc-500 mt-3">All review deadlines, schedules and email send times use this timezone.</p>
            </div>
          )}
          {tab === "users" && <UsersTab />}
          {tab === "access" && <AccessRequestsTab />}
          {tab === "security" && <SecurityTab />}
          {tab === "branding" && <BrandingTab s={s} set={set} />}
          {tab === "perspectives" && <PerspectivesSection s={s} set={set} />}
          {tab === "integrations" && <IntegrationsSection s={s} set={set} />}
          {tab === "email" && <EmailRoutingSection s={s} set={set} />}
          {tab === "rating" && <RatingAdjustmentSection s={s} set={set} />}
          {tab === "increase" && <IncreaseConfigSection s={s} set={set} />}
          {tab === "schedule" && <QuarterScheduleSection s={s} set={set} />}
        </section>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="mb-5">
      <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">{label}</label>
      <div className="mt-1.5">{children}</div>
      {hint && <div className="text-xs text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}

function FiscalSection({ s, set }) {
  // SARS convention: a fiscal year is named by the year in which it ENDS.
  // e.g. 1 July 2026 → 30 June 2027 = FY2027.
  const endYear = (() => {
    const m = (s.fiscal_year.label || "").match(/(\d{4})/);
    return m ? Number(m[1]) : new Date().getFullYear();
  })();
  // If start month <= end month, the FY sits within a single calendar year.
  // Otherwise the start lands in the prior calendar year.
  const startYear = (s.fiscal_year.start_month <= s.fiscal_year.end_month)
    ? endYear
    : endYear - 1;

  const startDate = new Date(startYear, (s.fiscal_year.start_month || 1) - 1, s.fiscal_year.start_day || 1);
  const endDate = new Date(endYear, (s.fiscal_year.end_month || 1) - 1, s.fiscal_year.end_day || 1);

  const fmt = (d) => d.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });

  const onPickStart = (d) => {
    if (!d) return;
    // Only persist month + day; the year is derived from the FY label + end month.
    set("fiscal_year.start_month", d.getMonth() + 1);
    set("fiscal_year.start_day", d.getDate());
  };
  const onPickEnd = (d) => {
    if (!d) return;
    // Update end month/day AND set the FY label to the year the FY ends (SARS convention).
    set("fiscal_year.end_month", d.getMonth() + 1);
    set("fiscal_year.end_day", d.getDate());
    set("fiscal_year.label", `FY${d.getFullYear()}`);
  };

  // What the label WOULD be by SARS convention given the current end-date selection.
  const derivedLabel = `FY${endDate.getFullYear()}`;

  return (
    <div className="max-w-2xl">
      <h2 className="text-xl font-semibold mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Fiscal year</h2>
      <p className="text-sm text-zinc-500 mb-6">
        Defines the performance review window. By SARS convention, a fiscal year is named by the year in which it <b>ends</b> — so a window of <i>1&nbsp;July&nbsp;2026&nbsp;→&nbsp;30&nbsp;June&nbsp;2027</i> is <b>FY2027</b>.
      </p>
      <div className="grid grid-cols-2 gap-5">
        <Field
          label="Year label"
          hint={s.fiscal_year.label !== derivedLabel
            ? `Auto-derived from end date: ${derivedLabel}. Pick the end date below to refresh.`
            : "Auto-updates from the end date you pick below (SARS convention)."}
        >
          <select className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white" value={s.fiscal_year.label} onChange={(e) => set("fiscal_year.label", e.target.value)}>
            {fyOptions().map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </Field>
        <div />
        <Field label="Start date" hint={`Falls in ${startYear} based on the end date.`}>
          <DatePickerField
            date={startDate}
            onPick={onPickStart}
            formatted={fmt(startDate)}
            testId="fiscal-start-picker"
          />
        </Field>
        <Field label="End date" hint="Picking a date here sets the Year label to FY{year}.">
          <DatePickerField
            date={endDate}
            onPick={onPickEnd}
            formatted={fmt(endDate)}
            testId="fiscal-end-picker"
          />
        </Field>
      </div>
    </div>
  );
}

function DatePickerField({ date, onPick, formatted, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className="w-full flex items-center justify-between border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white hover:bg-zinc-50"
        >
          <span>{formatted}</span>
          <CalendarIcon className="w-4 h-4 text-zinc-500" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          defaultMonth={date}
          onSelect={(d) => { onPick(d); setOpen(false); }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

function PerspectivesSection({ s, set }) {
  return (
    <div className="max-w-3xl">
      <h2 className="text-xl font-semibold mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Perspectives & rating scale</h2>
      <p className="text-sm text-zinc-500 mb-6">Appears as the legend on every locked KPA template document.</p>

      <Field label="Suggested perspectives (one per line)">
        <textarea rows={5} className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm font-mono" value={s.perspectives.join("\n")} onChange={(e) => set("perspectives", e.target.value.split("\n").filter(Boolean))} />
      </Field>

      <div className="mt-6">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600 mb-2">Rating scale (0–5)</div>
        <table className="w-full border border-zinc-300 text-sm">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-600">
            <tr><th className="px-3 py-2 text-left border-r border-zinc-300 w-16">Score</th><th className="px-3 py-2 text-left border-r border-zinc-300">Label</th><th className="px-3 py-2 text-left">Description</th></tr>
          </thead>
          <tbody>
            {s.rating_scale.map((r, i) => (
              <tr key={i} className="border-t border-zinc-200">
                <td className="px-3 py-2 border-r border-zinc-200"><input type="number" className="w-12 border border-zinc-300 px-2 py-1 rounded-sm" value={r.score} onChange={(e) => { const arr = [...s.rating_scale]; arr[i] = {...arr[i], score: Number(e.target.value)}; set("rating_scale", arr); }} /></td>
                <td className="px-3 py-2 border-r border-zinc-200"><input className="w-full border border-zinc-300 px-2 py-1 rounded-sm" value={r.label} onChange={(e) => { const arr = [...s.rating_scale]; arr[i] = {...arr[i], label: e.target.value}; set("rating_scale", arr); }} /></td>
                <td className="px-3 py-2"><input className="w-full border border-zinc-300 px-2 py-1 rounded-sm" value={r.description} onChange={(e) => { const arr = [...s.rating_scale]; arr[i] = {...arr[i], description: e.target.value}; set("rating_scale", arr); }} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function IntegrationCard({ title, enabled, canToggle = true, onToggle, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-zinc-300 rounded-sm mb-6">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`integration-toggle-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        className="w-full px-4 py-3 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between hover:bg-zinc-100"
      >
        <div className="flex items-center gap-3">
          <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
          <div className="text-sm font-semibold text-left">{title}</div>
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${enabled ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-zinc-100 text-zinc-700 border-zinc-200"}`}>
            {enabled ? "Enabled" : "Disabled"}
          </span>
        </div>
        {canToggle && (
          <label className="flex items-center gap-2 text-xs text-zinc-600" onClick={(e) => e.stopPropagation()}>
            <input type="checkbox" checked={enabled} onChange={(e) => onToggle(e.target.checked)} /> Enable
          </label>
        )}
      </button>
      {open && <div>{children}</div>}
    </div>
  );
}


function IntegrationsSection({ s, set }) {
  const sso = s.integrations.microsoft_sso;
  const mail = s.integrations.microsoft_graph_email;
  const od = s.integrations.microsoft_onedrive || { enabled: false, storage_upn: "", root_folder: "KPA" };
  const [testResult, setTestResult] = useState(null);
  const [migResult, setMigResult] = useState(null);
  const [busy, setBusy] = useState("");

  const testOd = async () => {
    setBusy("test"); setTestResult(null);
    try { const { data } = await api.post("/integrations/onedrive/test"); setTestResult({ ok: true, ...data }); toast.success("OneDrive reachable"); }
    catch (e) { setTestResult({ ok: false, error: formatApiError(e) }); }
    finally { setBusy(""); }
  };
  const migrateOd = async () => {
    if (!confirm("Migrate all existing base64 logos, policies and photos to OneDrive? This can take a few minutes.")) return;
    setBusy("migrate"); setMigResult(null);
    try { const { data } = await api.post("/integrations/onedrive/migrate"); setMigResult(data); toast.success(`Migrated ${data.policies}p / ${data.logos}l / ${data.photos}ph`); }
    catch (e) { setMigResult({ error: formatApiError(e) }); toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  return (
    <div className="max-w-3xl">
      <h2 className="text-xl font-semibold mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Integrations</h2>
      <p className="text-sm text-zinc-500 mb-6">Microsoft SSO, Graph email and OneDrive storage. All three share the same Azure AD app registration.</p>
      <Field label="User directory source">
        <select className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white" value={s.user_source} onChange={(e) => set("user_source", e.target.value)}>
          <option value="manual">Manual (managed in this app)</option>
          <option value="microsoft">Microsoft (pull from Microsoft Graph)</option>
        </select>
      </Field>

      <IntegrationCard title="Microsoft SSO (Azure AD)" enabled={sso.enabled}
        onToggle={(v) => set("integrations.microsoft_sso.enabled", v)}>
        <div className="p-4 grid grid-cols-2 gap-4">
          <Field label="Tenant ID"><input className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm" value={sso.tenant_id} onChange={(e) => set("integrations.microsoft_sso.tenant_id", e.target.value)} /></Field>
          <Field label="Client ID"><input className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm" value={sso.client_id} onChange={(e) => set("integrations.microsoft_sso.client_id", e.target.value)} /></Field>
          <Field label="Client Secret"><input type="password" className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm" value={sso.client_secret} onChange={(e) => set("integrations.microsoft_sso.client_secret", e.target.value)} /></Field>
          <Field label="Redirect URI"><input className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm" value={sso.redirect_uri} onChange={(e) => set("integrations.microsoft_sso.redirect_uri", e.target.value)} placeholder="https://your-domain/auth/microsoft/callback" /></Field>
        </div>
        <SetupGuide title="Microsoft SSO setup guide">
          <ol className="list-decimal pl-5 space-y-2">
            <li>Go to <a className="text-blue-600 underline" href="https://portal.azure.com" target="_blank" rel="noreferrer">portal.azure.com</a> → <b>Microsoft Entra ID</b> → <b>App registrations</b> → <b>New registration</b>.</li>
            <li>Name it <code className="bg-zinc-100 px-1">KPA Performance Manager</code>. Choose <b>Single tenant</b> (or Multi-tenant if you have multiple AD tenants).</li>
            <li>Under <b>Redirect URI</b>, choose <b>Web</b> and enter the value of <i>Redirect URI</i> above.</li>
            <li>Copy the <b>Application (client) ID</b> → <i>Client ID</i> above.</li>
            <li>Copy the <b>Directory (tenant) ID</b> → <i>Tenant ID</i>.</li>
            <li>Go to <b>Certificates &amp; secrets</b> → <b>New client secret</b> → copy the <b>Value</b> → <i>Client Secret</i>.</li>
            <li>Go to <b>API permissions</b> → <b>Microsoft Graph</b> → <b>Delegated permissions</b> → add <code>openid</code>, <code>profile</code>, <code>email</code>, <code>User.Read</code>. Click <b>Grant admin consent</b>.</li>
            <li>Toggle <b>Enable</b>, click <b>Save changes</b>.</li>
          </ol>
        </SetupGuide>
      </IntegrationCard>

      <IntegrationCard title="Microsoft Graph email (Outlook)" enabled={mail.enabled}
        onToggle={(v) => set("integrations.microsoft_graph_email.enabled", v)}>
        <div className="p-4 grid grid-cols-2 gap-4">
          <Field label="Tenant ID"><input className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm" value={mail.tenant_id} onChange={(e) => set("integrations.microsoft_graph_email.tenant_id", e.target.value)} /></Field>
          <Field label="Client ID"><input className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm" value={mail.client_id} onChange={(e) => set("integrations.microsoft_graph_email.client_id", e.target.value)} /></Field>
          <Field label="Client Secret"><input type="password" className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm" value={mail.client_secret} onChange={(e) => set("integrations.microsoft_graph_email.client_secret", e.target.value)} /></Field>
          <Field label="From address"><input className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm" value={mail.from_address} onChange={(e) => set("integrations.microsoft_graph_email.from_address", e.target.value)} placeholder="hr@company.com" /></Field>
        </div>
        <SetupGuide title="Microsoft Graph email setup guide">
          <ol className="list-decimal pl-5 space-y-2">
            <li>Use the same Azure AD app registration as SSO above.</li>
            <li>Under <b>API permissions</b> → <b>Microsoft Graph</b> → <b>Application permissions</b> → add <code>Mail.Send</code>. Click <b>Grant admin consent</b>.</li>
            <li>Set <i>From address</i> to a real Exchange mailbox in your tenant.</li>
            <li>Toggle <b>Enable</b>, click <b>Save changes</b>.</li>
          </ol>
        </SetupGuide>
      </IntegrationCard>

      <IntegrationCard title="Microsoft OneDrive (file storage)" enabled={od.enabled}
        onToggle={(v) => set("integrations.microsoft_onedrive.enabled", v)} defaultOpen>
        <div className="p-4 grid grid-cols-2 gap-4">
          <Field label="Storage UPN">
            <input
              data-testid="onedrive-upn-input"
              className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm"
              value={od.storage_upn}
              placeholder="kpa-storage@yourdomain.com"
              onChange={(e) => set("integrations.microsoft_onedrive.storage_upn", e.target.value)}
            />
          </Field>
          <Field label="Root folder in OneDrive">
            <input
              className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm"
              value={od.root_folder || "KPA"}
              onChange={(e) => set("integrations.microsoft_onedrive.root_folder", e.target.value)}
            />
          </Field>
          <div className="col-span-2 flex flex-wrap items-center gap-3">
            <button
              data-testid="onedrive-test-btn"
              type="button"
              onClick={testOd}
              disabled={busy === "test"}
              className="border border-zinc-300 px-3 py-2 rounded-sm text-sm hover:bg-zinc-50 disabled:opacity-60"
            >
              {busy === "test" ? "Testing…" : "Test connection"}
            </button>
            <button
              data-testid="onedrive-migrate-btn"
              type="button"
              onClick={migrateOd}
              disabled={busy === "migrate" || !od.enabled}
              className="border border-zinc-300 px-3 py-2 rounded-sm text-sm hover:bg-zinc-50 disabled:opacity-60"
              title={od.enabled ? "Move all existing logos, policies and photos into OneDrive" : "Enable OneDrive first"}
            >
              {busy === "migrate" ? "Migrating…" : "Migrate existing files to OneDrive"}
            </button>
            {od.last_test_at && <span className="text-[11px] text-zinc-500">Last tested: {od.last_test_at.split("T")[0]}</span>}
            {od.last_migration_at && <span className="text-[11px] text-zinc-500">Last migration: {od.last_migration_at.split("T")[0]}</span>}
          </div>
          {testResult && (
            <div className={`col-span-2 text-xs p-3 border rounded-sm ${testResult.ok ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-red-50 text-red-800 border-red-200"}`}
              data-testid="onedrive-test-result">
              {testResult.ok ? (
                <>Connected to <b>{testResult.drive_name || "OneDrive"}</b> ({testResult.drive_type}) — {testResult.quota?.total ? `${Math.round(testResult.quota.total / 1e9)} GB total` : "quota unknown"}</>
              ) : (
                <>Failed: {testResult.error}</>
              )}
            </div>
          )}
          {migResult && !migResult.error && (
            <div className="col-span-2 text-xs p-3 border rounded-sm bg-emerald-50 text-emerald-800 border-emerald-200" data-testid="onedrive-migrate-result">
              Migrated {migResult.policies} policies, {migResult.logos} logos, {migResult.photos} photos
              {migResult.errors && migResult.errors.length > 0 && <> · <b>{migResult.errors.length} errors</b> (check server logs)</>}
            </div>
          )}
        </div>
        <SetupGuide title="Microsoft OneDrive setup guide">
          <ol className="list-decimal pl-5 space-y-2">
            <li>Re-use the <b>same Azure AD app registration</b> you created for MS Sync.</li>
            <li>In the portal go to <b>API permissions</b> → <b>Add a permission</b> → <b>Microsoft Graph</b> → <b>Application permissions</b>.</li>
            <li>Search for and add <code className="bg-zinc-100 px-1">Files.ReadWrite.All</code>.</li>
            <li>Click <b>Grant admin consent for &lt;tenant&gt;</b> — the green check must appear next to the permission.</li>
            <li>Pick or create a dedicated Microsoft 365 user account whose OneDrive will hold KPA files (e.g. <code className="bg-zinc-100 px-1">kpa-storage@yourdomain.com</code>). Make sure that user has a OneDrive-enabled licence.</li>
            <li>Enter that user&apos;s email into <i>Storage UPN</i> above.</li>
            <li>Set <i>Root folder</i> (default <code className="bg-zinc-100 px-1">KPA</code>). Sub-folders <code>Policies</code>, <code>Logos</code> and <code>Photos</code> will be created automatically.</li>
            <li>Toggle <b>Enable</b>, click <b>Save changes</b>, then click <b>Test connection</b>.</li>
            <li>When the test passes, click <b>Migrate existing files to OneDrive</b> — all base64-stored logos, policy PDFs and profile photos are copied out of MongoDB into the OneDrive folders above, and the base64 payloads are removed from the DB. Safe to re-run (only remaining base64 files are moved).</li>
          </ol>
        </SetupGuide>
      </IntegrationCard>
    </div>
  );
}

function EmailRoutingSection({ s, set }) {
  const r = s.email_routing;
  return (
    <div className="max-w-2xl">
      <h2 className="text-xl font-semibold mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Email routing & schedule</h2>
      <p className="text-sm text-zinc-500 mb-6">When the system sends what, to whom, ahead of each deadline.</p>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Manager review notice (days before)"><input type="number" className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm" value={r.manager_review_lead_days} onChange={(e) => set("email_routing.manager_review_lead_days", Number(e.target.value))} /></Field>
        <Field label="Employee review notice (days before)"><input type="number" className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm" value={r.employee_review_lead_days} onChange={(e) => set("email_routing.employee_review_lead_days", Number(e.target.value))} /></Field>
        <Field label="Final meeting notice (days before)"><input type="number" className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm" value={r.final_meeting_lead_days} onChange={(e) => set("email_routing.final_meeting_lead_days", Number(e.target.value))} /></Field>
        <Field label="Reminder cadence (days)"><input type="number" className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm" value={r.reminder_cadence_days} onChange={(e) => set("email_routing.reminder_cadence_days", Number(e.target.value))} /></Field>
      </div>
      <label className="flex items-center gap-2 text-sm mt-2"><input type="checkbox" checked={r.cc_admin_on_all} onChange={(e) => set("email_routing.cc_admin_on_all", e.target.checked)} /> CC admin on all workflow emails</label>
    </div>
  );
}

function RatingAdjustmentSection({ s, set }) {
  const r = s.rating_adjustment;
  return (
    <div className="max-w-2xl">
      <h2 className="text-xl font-semibold mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Rating adjustment rules</h2>
      <p className="text-sm text-zinc-500 mb-6">Default rules for adjusting baseline ratings — shown as the <b>Increase %</b> legend on every template.</p>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Increase threshold (score ≥)"><input type="number" className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm" value={r.increase_threshold_score} onChange={(e) => set("rating_adjustment.increase_threshold_score", Number(e.target.value))} /></Field>
        <Field label="Increase %"><input type="number" step="0.1" className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm" value={r.increase_percent} onChange={(e) => set("rating_adjustment.increase_percent", Number(e.target.value))} /></Field>
        <Field label="Decrease threshold (score ≤)"><input type="number" className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm" value={r.decrease_threshold_score} onChange={(e) => set("rating_adjustment.decrease_threshold_score", Number(e.target.value))} /></Field>
        <Field label="Decrease %"><input type="number" step="0.1" className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm" value={r.decrease_percent} onChange={(e) => set("rating_adjustment.decrease_percent", Number(e.target.value))} /></Field>
        <Field label="Subjective score requiring Remcon approval (≥)"><input type="number" className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm" value={r.requires_remcon_approval_at} onChange={(e) => set("rating_adjustment.requires_remcon_approval_at", Number(e.target.value))} /></Field>
      </div>
    </div>
  );
}

function SetupGuide({ title, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-zinc-200">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50">
        <span>{title}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-4 pb-4 text-sm text-zinc-700">{children}</div>}
    </div>
  );
}

// ============ Users tab ============
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [bus, setBus] = useState([]);
  const [depts, setDepts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [welcomeInfo, setWelcomeInfo] = useState(null); // { name, email, temp_password, kind }
  const [selected, setSelected] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [buFilter, setBuFilter] = useState("all");
  const [msSyncOpen, setMsSyncOpen] = useState(false);

  // Filter users by free-text (name / email / title) and by BU dropdown.
  const filteredUsers = useMemo(() => {
    const s = search.trim().toLowerCase();
    return users.filter((u) => {
      if (buFilter !== "all") {
        if (buFilter === "none" ? !!u.business_unit_id : u.business_unit_id !== buFilter) return false;
      }
      if (!s) return true;
      return (
        (u.name || "").toLowerCase().includes(s) ||
        (u.email || "").toLowerCase().includes(s) ||
        (u.title || "").toLowerCase().includes(s) ||
        (u.role || "").toLowerCase().includes(s)
      );
    });
  }, [users, search, buFilter]);

  const load = async () => {
    const [u, b, d] = await Promise.all([
      api.get("/users"),
      api.get("/business-units").catch(() => ({ data: [] })),
      api.get("/departments").catch(() => ({ data: [] })),
    ]);
    setUsers(u.data); setBus(b.data); setDepts(d.data);
    setSelected(new Set());
  };
  useEffect(() => { load(); }, []);

  const toggleOne = (id) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleAll = () => setSelected((s) => {
    if (s.size === users.length) return new Set();
    return new Set(users.map((u) => u.id));
  });
  const bulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!confirm(`Delete ${ids.length} user${ids.length === 1 ? "" : "s"}? Their reviews will be removed, and references in Position Profiles/BUs will be cleared.`)) return;
    try {
      const { data } = await api.post("/users/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted?.users || 0} users · ${data.deleted?.reviews || 0} reviews cleaned`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async (data) => {
    try {
      const payload = {...data};
      if (data.id && !payload.password) delete payload.password;
      if (!data.id) delete payload.password; // never send password on create — let backend gen
      if (data.id) {
        await api.put(`/users/${data.id}`, payload);
        toast.success("Saved");
      } else {
        const { data: created } = await api.post("/users", payload);
        toast.success("User created — welcome email queued");
        setWelcomeInfo({ name: created.name, email: created.email, temp_password: created.temp_password, kind: "created" });
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resendWelcome = async (u) => {
    if (!confirm(`Resend welcome email to ${u.email}? Their existing password will be invalidated and replaced with a new temporary one.`)) return;
    try {
      const { data } = await api.post(`/users/${u.id}/resend-welcome`);
      setWelcomeInfo({ name: u.name, email: u.email, temp_password: data.temp_password, kind: "resent" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!confirm("Delete user?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const bname = (id) => bus.find((b) => b.id === id)?.name || "—";
  const dname = (id) => depts.find((d) => d.id === id)?.name || "—";

  const roleBadge = (r) => {
    const c = {
      super_admin: "bg-zinc-900 text-white",
      bu_owner: "bg-violet-100 text-violet-800 border border-violet-200",
      bu_manager: "bg-blue-100 text-blue-800 border border-blue-200",
      user: "bg-zinc-100 text-zinc-700 border border-zinc-200",
    };
    return <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm whitespace-nowrap ${c[r] || ""}`}>{r?.replace("_", " ")}</span>;
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Users</h2>
          <p className="text-sm text-zinc-500 mt-1">New users receive a welcome email with a temporary password and must change it on first sign-in.</p>
        </div>
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <button data-testid="users-bulk-delete-btn" onClick={bulkDelete}
              className="bg-red-600 text-white px-3 py-2 rounded-sm text-sm font-medium hover:bg-red-700 flex items-center gap-2">
              <Trash2 className="w-4 h-4" /> Delete {selected.size} selected
            </button>
          )}
          <button
            data-testid="users-sync-microsoft-btn"
            onClick={() => setMsSyncOpen(true)}
            className="border border-zinc-300 px-3 py-2 rounded-sm text-sm font-medium hover:bg-zinc-50 flex items-center gap-2 text-zinc-700"
            title="Preview and import licensed users from Microsoft Entra"
          >
            <Cloud className="w-4 h-4" /> Sync from Microsoft
          </button>
          <button data-testid="user-new-btn" onClick={() => setEditing({ email: "", name: "", title: "", role: "user", business_unit_id: null, department_id: null, line_manager_id: null })}
            style={{ background: "var(--brand-primary)" }}
            className="text-white px-4 py-2 rounded-sm text-sm font-medium hover:opacity-90 flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add user
          </button>
        </div>
      </div>

      {/* Search + BU filter toolbar */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            data-testid="users-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, title or role…"
            className="w-full border border-zinc-300 pl-9 pr-3 py-2 rounded-sm text-sm"
          />
        </div>
        <select
          data-testid="users-bu-filter"
          value={buFilter}
          onChange={(e) => setBuFilter(e.target.value)}
          className="border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white"
        >
          <option value="all">All Business Units</option>
          <option value="none">No BU assigned</option>
          {bus.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <div className="text-xs text-zinc-500">
          {filteredUsers.length}/{users.length} user{users.length === 1 ? "" : "s"}
        </div>
        {(search || buFilter !== "all") && (
          <button
            data-testid="users-filter-clear"
            onClick={() => { setSearch(""); setBuFilter("all"); }}
            className="text-xs text-zinc-500 hover:text-zinc-900 underline"
          >
            Clear
          </button>
        )}
      </div>

      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[1000px]">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="px-3 py-3 w-10">
                <input data-testid="users-select-all" type="checkbox"
                  checked={filteredUsers.length > 0 && filteredUsers.every((u) => selected.has(u.id))}
                  onChange={() => {
                    const allShownSelected = filteredUsers.length > 0 && filteredUsers.every((u) => selected.has(u.id));
                    setSelected((s) => {
                      const n = new Set(s);
                      if (allShownSelected) filteredUsers.forEach((u) => n.delete(u.id));
                      else filteredUsers.forEach((u) => n.add(u.id));
                      return n;
                    });
                  }}
                  className="cursor-pointer" />
              </th>
              <th className="text-left px-4 py-3 whitespace-nowrap">Name</th>
              <th className="text-left px-4 py-3 whitespace-nowrap">Email</th>
              <th className="text-left px-4 py-3 whitespace-nowrap">Role</th>
              <th className="text-left px-4 py-3 whitespace-nowrap">Business Unit</th>
              <th className="text-left px-4 py-3 whitespace-nowrap">Position Profile</th>
              <th className="text-left px-4 py-3 whitespace-nowrap">Status</th>
              <th className="px-4 py-3 w-44 whitespace-nowrap"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className={`border-t border-zinc-200 ${selected.has(u.id) ? "bg-blue-50/40" : ""}`}>
                <td className="px-3 py-3">
                  <input data-testid={`user-select-${u.id}`} type="checkbox"
                    checked={selected.has(u.id)} onChange={() => toggleOne(u.id)}
                    className="cursor-pointer" />
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium whitespace-nowrap">{u.name}</div>
                  <div className="text-xs text-zinc-500 whitespace-nowrap">{u.title}</div>
                </td>
                <td className="px-4 py-3 text-zinc-600 whitespace-nowrap">{u.email}</td>
                <td className="px-4 py-3 whitespace-nowrap">{roleBadge(u.role)}</td>
                <td className="px-4 py-3 whitespace-nowrap">{bname(u.business_unit_id)}</td>
                <td className="px-4 py-3 whitespace-nowrap">{dname(u.department_id)}</td>
                <td className="px-4 py-3">
                  {u.status === "pending_approval" ? (
                    <span className="inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border bg-amber-50 text-amber-800 border-amber-200 whitespace-nowrap">Pending approval</span>
                  ) : u.status === "denied" ? (
                    <span className="inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border bg-red-50 text-red-800 border-red-200 whitespace-nowrap">Denied</span>
                  ) : u.must_change_password ? (
                    <span className="inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border bg-amber-50 text-amber-800 border-amber-200 whitespace-nowrap">Pending sign-in</span>
                  ) : (
                    <span className="inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border bg-emerald-50 text-emerald-800 border-emerald-200 whitespace-nowrap">Active</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {u.status === "pending_approval" && (
                    <button data-testid={`user-approve-${u.id}`} onClick={async () => { try { await api.post(`/users/${u.id}/approve`); toast.success("Approved"); load(); } catch (e) { toast.error(formatApiError(e)); } }} className="text-xs bg-zinc-900 text-white px-3 py-1 rounded-sm hover:bg-zinc-800 mr-2">Approve</button>
                  )}
                  <button data-testid={`user-resend-${u.id}`} onClick={() => resendWelcome(u)} className="text-xs text-blue-600 hover:underline mr-3" title="Resend welcome email + new temporary password">Resend</button>
                  <button data-testid={`user-edit-${u.id}`} onClick={() => setEditing({ ...u })} className="text-zinc-600 hover:text-zinc-900 mr-3"><Pencil className="w-4 h-4 inline" /></button>
                  <button data-testid={`user-delete-${u.id}`} onClick={() => del(u.id)} className="text-red-600 hover:text-red-800"><Trash2 className="w-4 h-4 inline" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <UserModal initial={editing} bus={bus} depts={depts} users={users} onCancel={() => setEditing(null)} onSave={save} />
      )}
      {welcomeInfo && (
        <WelcomeInfoModal info={welcomeInfo} onClose={() => setWelcomeInfo(null)} />
      )}
      {msSyncOpen && (
        <MicrosoftSyncModal
          bus={bus}
          existingUsers={users}
          onClose={() => setMsSyncOpen(false)}
          onSynced={() => { setMsSyncOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function MicrosoftSyncModal({ bus, existingUsers = [], onClose, onSynced }) {
  // stage: 'pick-bu' -> 'loading' -> 'preview' -> 'committing'
  const [stage, setStage] = useState("pick-bu");
  const [defaultBu, setDefaultBu] = useState("");
  const [defaultRole, setDefaultRole] = useState("user");
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [showExistingOnly, setShowExistingOnly] = useState("all"); // all | new | existing
  // Per-row admin overrides keyed by ms_oid: { business_unit_id, manager_ref }
  //   manager_ref format: "ms:<msOid>" (pick from preview) | "kpa:<userId>" (pick existing KPA user) | "" (unset)
  const [overrides, setOverrides] = useState({});

  const setOverride = (msOid, patch) => setOverrides((prev) => ({
    ...prev,
    [msOid]: { ...(prev[msOid] || {}), ...patch },
  }));

  const fetchPreview = async () => {
    setStage("loading");
    setErrorMsg("");
    try {
      const { data } = await api.get("/microsoft/sync-preview");
      const users = data.users || [];
      setRows(users);
      // Pre-tick every user by default
      setSelected(new Set(users.map((u) => u.ms_oid)));
      // Seed overrides so each row starts with the "sane default" already selected
      const seed = {};
      users.forEach((u) => {
        seed[u.ms_oid] = {
          business_unit_id: defaultBu || "",
          // If MS manager is also in the preview list, pre-select them; else blank
          manager_ref: u.manager_ms_oid && u.manager_in_preview ? `ms:${u.manager_ms_oid}` : "",
        };
      });
      setOverrides(seed);
      setStage("preview");
    } catch (e) {
      setErrorMsg(formatApiError(e));
      setStage("pick-bu");
    }
  };

  const toggle = (oid) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(oid)) n.delete(oid); else n.add(oid);
    return n;
  });

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (showExistingOnly === "new" && r.existing_id) return false;
      if (showExistingOnly === "existing" && !r.existing_id) return false;
      if (!q) return true;
      return (
        (r.display_name || "").toLowerCase().includes(q) ||
        (r.email || "").toLowerCase().includes(q) ||
        (r.job_title || "").toLowerCase().includes(q) ||
        (r.department || "").toLowerCase().includes(q) ||
        (r.manager_display_name || "").toLowerCase().includes(q)
      );
    });
  }, [rows, search, showExistingOnly]);

  const toggleAllVisible = () => {
    const visibleOids = filteredRows.map((r) => r.ms_oid);
    const allSelected = visibleOids.every((o) => selected.has(o));
    setSelected((s) => {
      const n = new Set(s);
      if (allSelected) visibleOids.forEach((o) => n.delete(o));
      else visibleOids.forEach((o) => n.add(o));
      return n;
    });
  };

  // "Apply default BU to all rows" — one-click bulk override.
  const applyDefaultBuToAll = () => {
    setOverrides((prev) => {
      const n = { ...prev };
      rows.forEach((r) => {
        n[r.ms_oid] = { ...(n[r.ms_oid] || {}), business_unit_id: defaultBu || "" };
      });
      return n;
    });
    toast.success(defaultBu ? "Applied default BU to all rows" : "Cleared BU on all rows");
  };

  const commit = async () => {
    const chosen = rows.filter((r) => selected.has(r.ms_oid)).map((r) => {
      const o = overrides[r.ms_oid] || {};
      const out = { ...r };
      // per-row BU override (empty string means "no BU")
      out.business_unit_id = o.business_unit_id || null;
      // per-row manager override: split into ms_oid vs KPA user id
      const ref = (o.manager_ref || "").trim();
      if (ref.startsWith("ms:")) {
        out.manager_ms_oid = ref.slice(3);
        out.line_manager_user_id = null;
      } else if (ref.startsWith("kpa:")) {
        out.manager_ms_oid = null;
        out.line_manager_user_id = ref.slice(4);
      } else {
        // admin explicitly cleared it
        out.manager_ms_oid = null;
        out.line_manager_user_id = null;
      }
      return out;
    });
    if (!chosen.length) return toast.error("Select at least one user");
    setStage("committing");
    try {
      const { data } = await api.post("/microsoft/sync-commit", {
        users: chosen,
        default_business_unit_id: defaultBu || null,
        default_role: defaultRole,
      });
      toast.success(`Synced from Microsoft — ${data.created} created, ${data.updated} updated${data.skipped ? `, ${data.skipped} skipped` : ""}, ${data.manager_links} manager links`);
      onSynced();
    } catch (e) {
      toast.error(formatApiError(e));
      setStage("preview");
    }
  };

  const chosenCount = selected.size;
  const newCount = rows.filter((r) => !r.existing_id && selected.has(r.ms_oid)).length;
  const updCount = rows.filter((r) => r.existing_id && selected.has(r.ms_oid)).length;

  // Sorted list for the manager <select> — synced preview users first, then existing KPA users
  const managerOptions = useMemo(() => {
    const previewOpts = rows
      .map((r) => ({ value: `ms:${r.ms_oid}`, label: `${r.display_name || r.email}` + (r.existing_id ? " (KPA)" : " (Microsoft)") }))
      .sort((a, b) => a.label.localeCompare(b.label));
    // existing KPA users who aren't in the preview (matched by ms_oid), to allow admin to link an external manager
    const previewOids = new Set(rows.map((r) => r.ms_oid));
    const kpaOpts = existingUsers
      .filter((u) => !u.ms_oid || !previewOids.has(u.ms_oid))
      .map((u) => ({ value: `kpa:${u.id}`, label: `${u.name} — KPA only` }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [...previewOpts, ...kpaOpts];
  }, [rows, existingUsers]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="ms-sync-modal">
      <div
        className="bg-white border border-zinc-200 rounded-sm w-[97vw] max-h-[92vh] flex flex-col"
        style={{ maxWidth: "1700px" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Cloud className="w-4 h-4 text-blue-600" />
            <div className="text-sm font-semibold">Sync users from Microsoft Entra</div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900 text-lg leading-none">×</button>
        </div>

        {/* Stage 1: pick BU + kick off Graph fetch */}
        {stage === "pick-bu" && (
          <div className="p-6 space-y-4">
            <p className="text-sm text-zinc-600">
              Only <b>licensed Member</b> accounts will be pulled from your tenant. Guests and unlicensed
              accounts are ignored. You&apos;ll get a preview and can adjust each user&apos;s BU and manager
              before importing.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Default Business Unit for new users">
                <select
                  data-testid="ms-sync-default-bu"
                  value={defaultBu}
                  onChange={(e) => setDefaultBu(e.target.value)}
                  className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white"
                >
                  <option value="">— No BU (assign later) —</option>
                  {bus.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </Field>
              <Field label="Default role for new users">
                <select
                  data-testid="ms-sync-default-role"
                  value={defaultRole}
                  onChange={(e) => setDefaultRole(e.target.value)}
                  className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white"
                >
                  <option value="user">User</option>
                  <option value="bu_manager">BU Manager</option>
                  <option value="bu_owner">BU Owner</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </Field>
            </div>
            <p className="text-xs text-zinc-500">
              You&apos;ll be able to override BU and manager per user in the next step. Existing users
              are matched by Microsoft object ID or email; their role and password are always left
              untouched.
            </p>
            {errorMsg && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm p-3" data-testid="ms-sync-error">
                {errorMsg}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="px-4 py-2 text-sm border border-zinc-300 rounded-sm hover:bg-zinc-50">Cancel</button>
              <button
                data-testid="ms-sync-fetch-btn"
                onClick={fetchPreview}
                style={{ background: "var(--brand-primary)" }}
                className="text-white px-4 py-2 rounded-sm text-sm font-medium hover:opacity-90 flex items-center gap-2"
              >
                <Cloud className="w-4 h-4" /> Fetch licensed users
              </button>
            </div>
          </div>
        )}

        {stage === "loading" && (
          <div className="p-16 text-center text-sm text-zinc-500" data-testid="ms-sync-loading">
            Fetching licensed users from Microsoft Graph…
            <div className="text-xs mt-2 text-zinc-400">This can take up to a minute for large tenants.</div>
          </div>
        )}

        {stage === "committing" && (
          <div className="p-16 text-center text-sm text-zinc-500" data-testid="ms-sync-committing">
            Importing selected users…
          </div>
        )}

        {stage === "preview" && (
          <>
            <div className="px-5 py-3 border-b border-zinc-200 flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[220px] max-w-md">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  data-testid="ms-sync-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name / email / manager…"
                  className="w-full border border-zinc-300 pl-9 pr-3 py-2 rounded-sm text-sm"
                />
              </div>
              <select
                data-testid="ms-sync-status-filter"
                value={showExistingOnly}
                onChange={(e) => setShowExistingOnly(e.target.value)}
                className="border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white"
              >
                <option value="all">All ({rows.length})</option>
                <option value="new">Only new ({rows.filter((r) => !r.existing_id).length})</option>
                <option value="existing">Only existing ({rows.filter((r) => r.existing_id).length})</option>
              </select>
              <button
                data-testid="ms-sync-apply-default-bu"
                onClick={applyDefaultBuToAll}
                className="text-xs border border-zinc-300 px-3 py-2 rounded-sm hover:bg-zinc-50"
                title="Copy the default BU to every row"
              >
                Apply default BU to all
              </button>
              <button
                data-testid="ms-sync-export-btn"
                onClick={async () => {
                  try {
                    const resp = await api.post("/microsoft/sync-preview/export", { users: rows }, { responseType: "blob" });
                    const url = URL.createObjectURL(resp.data);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `microsoft-sync-preview-${new Date().toISOString().slice(0, 10)}.xlsx`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(url);
                    toast.success(`Exported ${rows.length} rows to Excel`);
                  } catch (e) {
                    toast.error(formatApiError(e));
                  }
                }}
                className="text-xs border border-zinc-300 px-3 py-2 rounded-sm hover:bg-zinc-50 flex items-center gap-1.5"
                title="Download the full preview list as an Excel workbook"
              >
                <Download className="w-3.5 h-3.5" /> Download Excel
              </button>
              <div className="text-xs text-zinc-500 ml-auto">
                <b>{chosenCount}</b> selected · {newCount} new · {updCount} existing
              </div>
            </div>

            <div className="flex-1 overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500 sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-3 w-10">
                      <input
                        data-testid="ms-sync-select-all"
                        type="checkbox"
                        checked={filteredRows.length > 0 && filteredRows.every((r) => selected.has(r.ms_oid))}
                        onChange={toggleAllVisible}
                        className="cursor-pointer"
                      />
                    </th>
                    <th className="text-left px-3 py-3 whitespace-nowrap">Name</th>
                    <th className="text-left px-3 py-3 whitespace-nowrap">Email</th>
                    <th className="text-left px-3 py-3 whitespace-nowrap">Title</th>
                    <th className="text-left px-3 py-3 whitespace-nowrap">MS Department</th>
                    <th className="text-left px-3 py-3 whitespace-nowrap">Business Unit</th>
                    <th className="text-left px-3 py-3 whitespace-nowrap">Line Manager</th>
                    <th className="text-left px-3 py-3 whitespace-nowrap">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r) => {
                    const o = overrides[r.ms_oid] || {};
                    return (
                      <tr
                        key={r.ms_oid}
                        className={`border-t border-zinc-100 ${selected.has(r.ms_oid) ? "bg-blue-50/40" : ""} hover:bg-zinc-50`}
                      >
                        <td className="px-3 py-2">
                          <input
                            data-testid={`ms-sync-row-${r.ms_oid}`}
                            type="checkbox"
                            checked={selected.has(r.ms_oid)}
                            onChange={() => toggle(r.ms_oid)}
                            className="cursor-pointer"
                          />
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap font-medium">{r.display_name}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-zinc-600">{r.email}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-zinc-600">{r.job_title || "—"}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-zinc-500 italic">{r.department || "—"}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <select
                            data-testid={`ms-sync-bu-${r.ms_oid}`}
                            value={o.business_unit_id || ""}
                            onChange={(e) => setOverride(r.ms_oid, { business_unit_id: e.target.value })}
                            className="border border-zinc-300 px-2 py-1 rounded-sm text-sm bg-white min-w-[160px]"
                          >
                            <option value="">— No BU —</option>
                            {bus.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                          </select>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <select
                            data-testid={`ms-sync-mgr-${r.ms_oid}`}
                            value={o.manager_ref || ""}
                            onChange={(e) => setOverride(r.ms_oid, { manager_ref: e.target.value })}
                            className="border border-zinc-300 px-2 py-1 rounded-sm text-sm bg-white min-w-[220px]"
                          >
                            <option value="">— No manager —</option>
                            {managerOptions
                              .filter((opt) => opt.value !== `ms:${r.ms_oid}`)
                              .map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                          </select>
                          {r.manager_display_name && (
                            <div className="text-[10px] text-zinc-400 mt-0.5">MS: {r.manager_display_name}{!r.manager_in_preview && " (unlicensed)"}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {r.existing_id ? (
                            <span className="inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border bg-zinc-100 text-zinc-700 border-zinc-200">Existing</span>
                          ) : (
                            <span className="inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border bg-emerald-50 text-emerald-800 border-emerald-200">New</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {filteredRows.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-zinc-500">No users match this filter</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="px-5 py-3 border-t border-zinc-200 flex items-center justify-between">
              <div className="text-xs text-zinc-500">
                Default BU: <b>{bus.find((b) => b.id === defaultBu)?.name || "None"}</b> · Default role: <b>{defaultRole.replace("_", " ")}</b>
              </div>
              <div className="flex gap-2">
                <button onClick={onClose} className="px-4 py-2 text-sm border border-zinc-300 rounded-sm hover:bg-zinc-50">Cancel</button>
                <button
                  data-testid="ms-sync-commit-btn"
                  disabled={chosenCount === 0}
                  onClick={commit}
                  style={{ background: chosenCount === 0 ? "#a1a1aa" : "var(--brand-primary)" }}
                  className="text-white px-4 py-2 rounded-sm text-sm font-medium hover:opacity-90 disabled:cursor-not-allowed"
                >
                  Sync {chosenCount} user{chosenCount === 1 ? "" : "s"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


function WelcomeInfoModal({ info, onClose }) {
  const copy = () => { navigator.clipboard?.writeText(info.temp_password); toast.success("Copied"); };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white border border-zinc-200 rounded-sm max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-zinc-200 text-sm font-semibold">
          {info.kind === "created" ? "User created — welcome email queued" : "Welcome email re-issued"}
        </div>
        <div className="p-5 space-y-4 text-sm">
          <p className="text-zinc-600">
            A welcome email has been queued for <b>{info.name}</b> ({info.email}). Microsoft Graph email is not yet wired, so until it is you can share this temporary password with them directly:
          </p>
          <div className="bg-zinc-50 border border-zinc-300 rounded-sm p-3 flex items-center justify-between">
            <code className="font-mono text-base tracking-wider" data-testid="welcome-temp-password">{info.temp_password}</code>
            <button onClick={copy} data-testid="welcome-copy-btn" className="text-xs border border-zinc-300 rounded-sm px-3 py-1 bg-white hover:bg-zinc-100">Copy</button>
          </div>
          <p className="text-xs text-zinc-500">
            They&apos;ll be required to change it on first sign-in. Once Microsoft Graph is enabled in Settings → Integrations, this temporary password will be emailed automatically and you won&apos;t see it here.
          </p>
          <div className="flex justify-end">
            <button onClick={onClose} className="px-4 py-2 text-sm bg-zinc-900 text-white rounded-sm hover:bg-zinc-800" data-testid="welcome-close-btn">Done</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function UserModal({ initial, bus, depts, users, onCancel, onSave }) {
  const [v, setV] = useState(initial);
  const filteredDepts = depts.filter((d) => !v.business_unit_id || d.business_unit_id === v.business_unit_id);
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onCancel}>
      <div className="bg-white border border-zinc-200 rounded-sm max-w-xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-zinc-200 text-sm font-semibold">{v.id ? "Edit user" : "Add user"}</div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Name"><input data-testid="user-name-input" className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm" value={v.name} onChange={(e) => setV({...v, name: e.target.value})} /></Field>
            <Field label="Title"><input className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm" value={v.title || ""} onChange={(e) => setV({...v, title: e.target.value})} /></Field>
            <Field label="Email"><input data-testid="user-email-input" disabled={!!v.id} className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm disabled:bg-zinc-50" value={v.email} onChange={(e) => setV({...v, email: e.target.value})} /></Field>
            <Field label="Role">
              <select data-testid="user-role-select" className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white" value={v.role} onChange={(e) => setV({...v, role: e.target.value})}>
                <option value="super_admin">Super Admin</option>
                <option value="bu_owner">BU Owner</option>
                <option value="bu_manager">BU Manager</option>
                <option value="user">User</option>
              </select>
            </Field>
            <Field label="Business Unit">
              <select className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white" value={v.business_unit_id || ""} onChange={(e) => setV({...v, business_unit_id: e.target.value || null, department_id: null})}>
                <option value="">—</option>
                {bus.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </Field>
            <Field label="Position Profile">
              <select className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white" value={v.department_id || ""} onChange={(e) => setV({...v, department_id: e.target.value || null})}>
                <option value="">—</option>
                {filteredDepts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </Field>
            <Field label="Line manager">
              <select className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white" value={v.line_manager_id || ""} onChange={(e) => setV({...v, line_manager_id: e.target.value || null})}>
                <option value="">—</option>
                {users.filter((u) => u.role !== "user" && u.id !== v.id).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </Field>
            <Field label={v.id ? "Reset password (optional)" : "Initial password"}>
              {v.id ? (
                <input className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm" type="text" value={v.password || ""} placeholder="Leave blank to keep" onChange={(e) => setV({...v, password: e.target.value})} />
              ) : (
                <div className="border border-dashed border-zinc-300 bg-zinc-50 text-xs text-zinc-600 px-3 py-2 rounded-sm">
                  Auto-generated. We&apos;ll email the user a one-time password and prompt them to set their own on first sign-in.
                </div>
              )}
            </Field>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={onCancel} className="px-4 py-2 text-sm border border-zinc-300 rounded-sm hover:bg-zinc-50">Cancel</button>
            <button data-testid="user-save-btn" onClick={() => onSave(v)} className="px-4 py-2 text-sm bg-zinc-900 text-white rounded-sm hover:bg-zinc-800">Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AccessRequestsTab() {
  const [requests, setRequests] = useState([]);

  const load = async () => {
    try { const { data } = await api.get("/password-reset-requests"); setRequests(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    if (!confirm("Approve this password reset? A fresh temporary password will be queued to the user via email. It will NOT be displayed on screen.")) return;
    try {
      const { data } = await api.post(`/password-reset-requests/${id}/approve`);
      toast.success(`Reset approved. A welcome email has been queued for ${data.email}.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deny = async (id) => {
    if (!confirm("Deny this password reset request?")) return;
    try { await api.post(`/password-reset-requests/${id}/deny`); toast.success("Denied"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const pending = requests.filter((r) => r.status === "pending");
  const resolved = requests.filter((r) => r.status !== "pending").slice(0, 25);

  const statusBadge = (s) => {
    const c = {
      pending: "bg-amber-50 text-amber-800 border-amber-200",
      approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
      denied: "bg-red-50 text-red-800 border-red-200",
    }[s] || "";
    return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${c}`}>{s}</span>;
  };

  return (
    <div className="max-w-4xl">
      <h2 className="text-xl font-semibold mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Access Requests</h2>
      <p className="text-sm text-zinc-500 mb-6">
        Users who click "Forgot password" on the sign-in page appear here. Approving issues a fresh temporary password and queues it to the user via email — the password is never shown in the UI.
      </p>

      <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600 mb-2">Pending ({pending.length})</div>
      <div className="border border-zinc-200 bg-white mb-8">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left px-5 py-3">Name / Email</th>
              <th className="text-left px-5 py-3">Requested</th>
              <th className="px-5 py-3 w-44 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {pending.map((r) => (
              <tr key={r.id} className="border-t border-zinc-200">
                <td className="px-5 py-3">
                  <div className="font-medium">{r.name || "—"}</div>
                  <div className="text-xs text-zinc-500">{r.email}</div>
                </td>
                <td className="px-5 py-3 text-zinc-600">{new Date(r.requested_at).toLocaleString()}</td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <button data-testid={`access-approve-${r.id}`} onClick={() => approve(r.id)} className="text-xs bg-zinc-900 text-white px-3 py-1.5 rounded-sm hover:bg-zinc-800 mr-2">Approve</button>
                  <button data-testid={`access-deny-${r.id}`} onClick={() => deny(r.id)} className="text-xs border border-zinc-300 px-3 py-1.5 rounded-sm hover:bg-zinc-50">Deny</button>
                </td>
              </tr>
            ))}
            {pending.length === 0 && (
              <tr><td colSpan={3} className="px-5 py-10 text-center text-zinc-500 text-sm">No pending requests.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600 mb-2">History</div>
      <div className="border border-zinc-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left px-5 py-3">Email</th>
              <th className="text-left px-5 py-3">Requested</th>
              <th className="text-left px-5 py-3">Resolved</th>
              <th className="text-left px-5 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {resolved.map((r) => (
              <tr key={r.id} className="border-t border-zinc-200">
                <td className="px-5 py-3">{r.email}</td>
                <td className="px-5 py-3 text-zinc-600">{new Date(r.requested_at).toLocaleString()}</td>
                <td className="px-5 py-3 text-zinc-600">{r.resolved_at ? new Date(r.resolved_at).toLocaleString() : "—"}</td>
                <td className="px-5 py-3">{statusBadge(r.status)}</td>
              </tr>
            ))}
            {resolved.length === 0 && (
              <tr><td colSpan={4} className="px-5 py-10 text-center text-zinc-500 text-sm">No history yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SecurityTab() {
  const [byIp, setByIp] = useState([]);
  const [recent, setRecent] = useState([]);

  const load = async () => {
    try {
      const [a, r] = await Promise.all([
        api.get("/spam-attempts/by-ip"),
        api.get("/spam-attempts"),
      ]);
      setByIp(a.data); setRecent(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const totalAttempts = byIp.reduce((a, r) => a + (r.count || 0), 0);
  const blockedIps = byIp.filter((r) => r.count >= 10).length;

  return (
    <div className="max-w-4xl">
      <h2 className="text-xl font-semibold mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Security</h2>
      <p className="text-sm text-zinc-500 mb-6">
        Honeypot &amp; rate-limit trips on public auth endpoints. Each record auto-expires after 30 days. IPs with 10+ attempts in 24h are auto-blocked at the registration endpoint.
      </p>

      <div className="grid grid-cols-3 gap-3 mb-8">
        <SummaryStat label="Spam attempts (last 30 days)" value={totalAttempts} />
        <SummaryStat label="Distinct offending IPs" value={byIp.length} />
        <SummaryStat label="Currently auto-blocked" value={blockedIps} hint="≥10 attempts in 24h" />
      </div>

      <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600 mb-2">Top offenders</div>
      <div className="border border-zinc-200 bg-white mb-8">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500"><tr>
            <th className="text-left px-5 py-3">IP</th>
            <th className="text-left px-5 py-3">Attempts</th>
            <th className="text-left px-5 py-3">Last seen</th>
            <th className="text-left px-5 py-3">Sample emails</th>
            <th className="text-left px-5 py-3">Status</th>
          </tr></thead>
          <tbody>
            {byIp.map((r) => (
              <tr key={r.ip} className="border-t border-zinc-200">
                <td className="px-5 py-3 font-mono text-xs">{r.ip}</td>
                <td className="px-5 py-3 tabular-nums">{r.count}</td>
                <td className="px-5 py-3 text-zinc-600">{r.last_seen ? new Date(r.last_seen).toLocaleString() : "—"}</td>
                <td className="px-5 py-3 text-zinc-600 truncate max-w-xs">{(r.emails || []).slice(0, 3).join(", ")}{r.emails?.length > 3 ? "…" : ""}</td>
                <td className="px-5 py-3">
                  {r.count >= 10
                    ? <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border bg-red-50 text-red-800 border-red-200">Auto-blocked</span>
                    : <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border bg-amber-50 text-amber-800 border-amber-200">Watch</span>}
                </td>
              </tr>
            ))}
            {byIp.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-zinc-500 text-sm">No spam attempts logged yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600 mb-2">Recent attempts</div>
      <div className="border border-zinc-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500"><tr>
            <th className="text-left px-5 py-3">When</th>
            <th className="text-left px-5 py-3">IP</th>
            <th className="text-left px-5 py-3">Attempted email</th>
            <th className="text-left px-5 py-3">Name</th>
            <th className="text-left px-5 py-3">Kind</th>
          </tr></thead>
          <tbody>
            {recent.slice(0, 50).map((r) => (
              <tr key={r.id} className="border-t border-zinc-200">
                <td className="px-5 py-3 text-zinc-600 whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</td>
                <td className="px-5 py-3 font-mono text-xs">{r.ip}</td>
                <td className="px-5 py-3 text-zinc-600">{r.email}</td>
                <td className="px-5 py-3 text-zinc-600">{r.attempted_name}</td>
                <td className="px-5 py-3"><span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border bg-zinc-50 text-zinc-700 border-zinc-200">{r.kind}</span></td>
              </tr>
            ))}
            {recent.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-zinc-500 text-sm">No attempts logged.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SummaryStat({ label, value, hint }) {
  return (
    <div className="border border-zinc-200 bg-white p-4">
      <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">{label}</div>
      <div className="mt-2 text-2xl font-bold text-zinc-900 tabular-nums" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{value}</div>
      {hint && <div className="text-xs text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}

function BrandingTab({ s, set }) {
  const fileToDataUrl = (file, cb) => {
    const reader = new FileReader();
    reader.onload = () => cb(reader.result);
    reader.readAsDataURL(file);
  };
  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1024 * 1024) return toast.error("Logo must be under 1 MB");
    fileToDataUrl(f, (url) => set("app_logo", url));
  };
  const primary = s.brand_primary || "#2C7BB6";
  const accent = s.brand_accent || "#1f2937";
  const sidebar = s.brand_sidebar || "#1f2937";
  const font = s.brand_font || "'IBM Plex Sans', system-ui, sans-serif";
  // Live-preview by writing the CSS vars immediately so the rest of the app reflects edits before save.
  React.useEffect(() => {
    document.documentElement.style.setProperty("--brand-primary", primary);
    document.documentElement.style.setProperty("--brand-accent", accent);
    document.documentElement.style.setProperty("--brand-sidebar", sidebar);
    document.documentElement.style.setProperty("--brand-font", font);
    document.body.style.fontFamily = font;
    const familyName = font.split(",")[0].replace(/['"]/g, "").trim();
    if (GFONT_LIST.includes(familyName)) {
      const id = `gfont-${familyName.replace(/\s+/g, "-")}`;
      if (!document.getElementById(id)) {
        const link = document.createElement("link");
        link.id = id;
        link.rel = "stylesheet";
        link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(familyName)}:wght@400;500;600;700&display=swap`;
        document.head.appendChild(link);
      }
    }
  }, [primary, accent, sidebar, font]);
  return (
    <div className="max-w-3xl">
      <h2 className="text-xl font-semibold mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>App branding</h2>
      <p className="text-sm text-zinc-500 mb-6">Logo, colours and typography for the KPA Rater. Changes preview live — click <b>Save changes</b> to persist them.</p>

      <div className="grid grid-cols-2 gap-6 mb-8">
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">App name</label>
          <input className="w-full mt-1 border border-zinc-300 px-3 py-2 rounded-sm text-sm" value={s.app_name || "KPA Performance Manager"} onChange={(e) => set("app_name", e.target.value)} data-testid="branding-app-name" />
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">App logo (PNG/SVG, &lt; 1 MB)</label>
          {s.app_logo ? (
            <div className="mt-2 flex flex-col items-start gap-2">
              <img src={s.app_logo} alt="app logo" className="max-h-16 max-w-[180px] object-contain" />
              <div className="flex items-center gap-2">
                <label
                  data-testid="branding-app-logo-replace"
                  title="Replace logo"
                  className="p-1.5 border border-zinc-300 rounded-sm hover:bg-zinc-50 cursor-pointer text-zinc-600 hover:text-zinc-900"
                >
                  <Upload className="w-3.5 h-3.5" />
                  <input type="file" accept="image/*" onChange={onPick} className="hidden" data-testid="branding-app-logo-input" />
                </label>
                <button
                  data-testid="branding-app-logo-delete"
                  title="Remove logo"
                  onClick={() => set("app_logo", null)}
                  className="p-1.5 border border-zinc-300 rounded-sm hover:bg-red-50 hover:border-red-300 text-zinc-600 hover:text-red-700"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ) : (
            <label className="mt-2 inline-flex items-center gap-1 text-xs border border-zinc-300 px-3 py-1.5 rounded-sm hover:bg-zinc-50 cursor-pointer" data-testid="branding-app-logo-upload-label">
              <Upload className="w-3.5 h-3.5" /> Upload logo
              <input type="file" accept="image/*" onChange={onPick} className="hidden" data-testid="branding-app-logo-input" />
            </label>
          )}
        </div>
      </div>

      <div className="border-t border-zinc-200 pt-6 mb-6">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-700 mb-4">Theme colours</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <ColorField
            label="Primary"
            hint="Buttons, active nav, badges"
            value={primary}
            onChange={(v) => set("brand_primary", v)}
            onReset={() => set("brand_primary", "#2C7BB6")}
            testIdColor="branding-primary-color"
            testIdHex="branding-primary-hex"
          />
          <ColorField
            label="Accent"
            hint="Highlights, headings"
            value={accent}
            onChange={(v) => set("brand_accent", v)}
            onReset={() => set("brand_accent", "#1f2937")}
            testIdColor="branding-accent-color"
            testIdHex="branding-accent-hex"
          />
          <ColorField
            label="Sidebar"
            hint="Sidebar background — pick a dark shade"
            value={sidebar}
            onChange={(v) => set("brand_sidebar", v)}
            onReset={() => set("brand_sidebar", "#1f2937")}
            testIdColor="branding-sidebar-color"
            testIdHex="branding-sidebar-hex"
          />
        </div>
      </div>

      <div className="border-t border-zinc-200 pt-6 mb-6">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-700 mb-4">Typography</h3>
        <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Font family</label>
        <select
          value={font}
          onChange={(e) => set("brand_font", e.target.value)}
          data-testid="branding-font-select"
          className="w-full mt-2 border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white"
          style={{ fontFamily: font }}
        >
          {FONT_OPTIONS.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.fonts.map((f) => (
                <option key={f.label} value={f.value} style={{ fontFamily: f.value }}>{f.label}</option>
              ))}
            </optgroup>
          ))}
        </select>
        <p className="text-xs text-zinc-500 mt-2">Google fonts load lazily when selected. System fonts have zero load cost.</p>
      </div>

      {/* Live preview */}
      <div className="border-t border-zinc-200 pt-6">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-700 mb-4">Preview</h3>
        <div className="border border-zinc-200 rounded-sm p-6 bg-zinc-50" style={{ fontFamily: font }}>
          <div className="text-3xl font-bold" style={{ color: accent }}>The quick brown fox</div>
          <div className="text-base text-zinc-600 mt-1">jumps over the lazy dog — 1234567890</div>
          <div className="flex gap-2 mt-4">
            <button style={{ background: primary }} className="text-white px-4 py-2 rounded-sm text-sm font-medium hover:opacity-90">Primary action</button>
            <button style={{ borderColor: primary, color: primary }} className="border-2 px-4 py-2 rounded-sm text-sm font-medium hover:opacity-80">Secondary</button>
            <span style={{ background: primary }} className="text-white text-[10px] min-w-[18px] h-[18px] px-1.5 rounded-full font-semibold inline-flex items-center justify-center">12</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Curated font selection. "value" is the CSS font-family stack written to --brand-font.
function ColorField({ label, hint, value, onChange, onReset, testIdColor, testIdHex }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-700 truncate">{label}</div>
        <button
          type="button"
          onClick={onReset}
          className="text-[10px] uppercase tracking-wider text-zinc-400 hover:text-zinc-700"
        >
          Reset
        </button>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testIdColor}
          className="h-9 w-12 rounded-sm border border-zinc-300 cursor-pointer p-0 flex-shrink-0"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testIdHex}
          className="min-w-0 flex-1 h-9 border border-zinc-300 px-2 rounded-sm text-xs font-mono"
        />
      </div>
      {hint && <div className="text-[10px] text-zinc-500 mt-2">{hint}</div>}
    </div>
  );
}

const FONT_OPTIONS = [
  { label: "Currently loaded", fonts: [
    { label: "IBM Plex Sans (default)", value: "'IBM Plex Sans', system-ui, sans-serif" },
    { label: "Cabinet Grotesk", value: "'Cabinet Grotesk', 'IBM Plex Sans', sans-serif" },
    { label: "Inter", value: "Inter, system-ui, sans-serif" },
  ]},
  { label: "System stacks (no load)", fonts: [
    { label: "System UI", value: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" },
    { label: "Helvetica / Arial", value: "Helvetica, Arial, sans-serif" },
    { label: "Verdana", value: "Verdana, Geneva, sans-serif" },
    { label: "Tahoma", value: "Tahoma, Geneva, sans-serif" },
    { label: "Trebuchet MS", value: "'Trebuchet MS', sans-serif" },
    { label: "Georgia (serif)", value: "Georgia, 'Times New Roman', serif" },
    { label: "Times New Roman (serif)", value: "'Times New Roman', Times, serif" },
    { label: "Courier New (mono)", value: "'Courier New', Courier, monospace" },
    { label: "Menlo / Monaco (mono)", value: "Menlo, Monaco, 'Courier New', monospace" },
  ]},
  { label: "Modern sans-serif (Google Fonts)", fonts: [
    { label: "Roboto", value: "'Roboto', system-ui, sans-serif" },
    { label: "Open Sans", value: "'Open Sans', system-ui, sans-serif" },
    { label: "Lato", value: "'Lato', system-ui, sans-serif" },
    { label: "Poppins", value: "'Poppins', system-ui, sans-serif" },
    { label: "Montserrat", value: "'Montserrat', system-ui, sans-serif" },
    { label: "Source Sans 3", value: "'Source Sans 3', system-ui, sans-serif" },
    { label: "Nunito", value: "'Nunito', system-ui, sans-serif" },
    { label: "Raleway", value: "'Raleway', system-ui, sans-serif" },
    { label: "Work Sans", value: "'Work Sans', system-ui, sans-serif" },
    { label: "Plus Jakarta Sans", value: "'Plus Jakarta Sans', system-ui, sans-serif" },
    { label: "DM Sans", value: "'DM Sans', system-ui, sans-serif" },
    { label: "Outfit", value: "'Outfit', system-ui, sans-serif" },
    { label: "Manrope", value: "'Manrope', system-ui, sans-serif" },
    { label: "Space Grotesk", value: "'Space Grotesk', system-ui, sans-serif" },
    { label: "Fira Sans", value: "'Fira Sans', system-ui, sans-serif" },
    { label: "Karla", value: "'Karla', system-ui, sans-serif" },
    { label: "Mulish", value: "'Mulish', system-ui, sans-serif" },
    { label: "Rubik", value: "'Rubik', system-ui, sans-serif" },
    { label: "Quicksand", value: "'Quicksand', system-ui, sans-serif" },
    { label: "PT Sans", value: "'PT Sans', system-ui, sans-serif" },
    { label: "Noto Sans", value: "'Noto Sans', system-ui, sans-serif" },
  ]},
  { label: "Serif (Google Fonts)", fonts: [
    { label: "Playfair Display", value: "'Playfair Display', Georgia, serif" },
    { label: "Merriweather", value: "'Merriweather', Georgia, serif" },
    { label: "Lora", value: "'Lora', Georgia, serif" },
    { label: "Bitter", value: "'Bitter', Georgia, serif" },
    { label: "Crimson Text", value: "'Crimson Text', Georgia, serif" },
    { label: "EB Garamond", value: "'EB Garamond', Georgia, serif" },
    { label: "Cormorant Garamond", value: "'Cormorant Garamond', Georgia, serif" },
  ]},
  { label: "Monospace", fonts: [
    { label: "JetBrains Mono", value: "'JetBrains Mono', 'Courier New', monospace" },
  ]},
];

const GFONT_LIST = [
  "Inter", "Roboto", "Open Sans", "Lato", "Montserrat", "Poppins",
  "Source Sans 3", "Nunito", "Raleway", "Work Sans", "Plus Jakarta Sans",
  "DM Sans", "Outfit", "Manrope", "Space Grotesk", "Playfair Display",
  "Merriweather", "Lora", "JetBrains Mono", "Fira Sans", "Karla",
  "Mulish", "Rubik", "Quicksand", "PT Sans", "Noto Sans", "Bitter",
  "Crimson Text", "EB Garamond", "Cormorant Garamond",
];


// ─────────────────────────────────────────────────────────────
// Increase configuration — the score → % table used by cycles.
// ─────────────────────────────────────────────────────────────
function IncreaseConfigSection({ s, set }) {
  const cfg = s.increase_config || {
    base_score: "average", buckets: [], pro_rata_enabled: true, min_months_full: 12,
  };
  const setCfg = (patch) => set("increase_config", { ...cfg, ...patch });

  const updateBucket = (i, patch) => {
    const next = (cfg.buckets || []).slice();
    next[i] = { ...next[i], ...patch };
    setCfg({ buckets: next });
  };
  const addBucket = () => {
    setCfg({ buckets: [...(cfg.buckets || []), { min_score: 0, max_score: 0, percentage: 0, label: "" }] });
  };
  const removeBucket = (i) => {
    setCfg({ buckets: (cfg.buckets || []).filter((_, idx) => idx !== i) });
  };

  return (
    <section data-testid="settings-increase-section" className="max-w-3xl">
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Increase configuration</div>
        <h2 className="text-2xl font-semibold mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Score → percentage schedule</h2>
        <p className="text-xs text-zinc-500 mt-1">
          These buckets drive the annual salary-increase calculation. Add as many rows as you need — each has a score
          range (inclusive) and the % to apply. Overlapping ranges: the first matching row wins.
        </p>
      </div>

      <div className="border border-zinc-200 rounded-sm p-4 mb-4 bg-white">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Base score for calculation</div>
            <select value={cfg.base_score || "average"}
              onChange={(e) => setCfg({ base_score: e.target.value })}
              data-testid="cfg-base-score"
              className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white">
              <option value="average">Average of Q1–Q4 final scores</option>
              <option value="q4">Q4 final score only</option>
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Minimum months for full increase</div>
            <input type="number" min="0" max="24"
              value={cfg.min_months_full ?? 12}
              onChange={(e) => setCfg({ min_months_full: Number(e.target.value) })}
              data-testid="cfg-min-months"
              className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm mt-3">
          <input type="checkbox" checked={!!cfg.pro_rata_enabled}
            data-testid="cfg-pro-rata"
            onChange={(e) => setCfg({ pro_rata_enabled: e.target.checked })} />
          Apply pro-rata for employees hired within the last {cfg.min_months_full ?? 12} months
        </label>
      </div>

      <div className="border border-zinc-200 rounded-sm bg-white overflow-hidden">
        <div className="px-4 py-2 border-b border-zinc-200 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">Percentage buckets</div>
          <button onClick={addBucket} data-testid="cfg-add-bucket"
            className="text-xs text-emerald-700 hover:text-emerald-900 flex items-center gap-1">
            + Add bucket
          </button>
        </div>
        <table className="min-w-full text-sm">
          <thead className="bg-zinc-50">
            <tr>
              {["Label", "Min score", "Max score", "Percentage %", ""].map((h) => (
                <th key={h} className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(cfg.buckets || []).map((b, i) => (
              <tr key={i} className="border-t border-zinc-100" data-testid={`cfg-bucket-${i}`}>
                <td className="px-3 py-2"><input value={b.label || ""} onChange={(e) => updateBucket(i, { label: e.target.value })}
                  className="w-full border border-zinc-200 px-2 py-1 rounded-sm text-sm bg-white" /></td>
                <td className="px-3 py-2"><input type="number" step="0.1" value={b.min_score ?? 0}
                  onChange={(e) => updateBucket(i, { min_score: Number(e.target.value) })}
                  className="w-20 border border-zinc-200 px-2 py-1 rounded-sm text-sm bg-white tabular-nums" /></td>
                <td className="px-3 py-2"><input type="number" step="0.1" value={b.max_score ?? 0}
                  onChange={(e) => updateBucket(i, { max_score: Number(e.target.value) })}
                  className="w-20 border border-zinc-200 px-2 py-1 rounded-sm text-sm bg-white tabular-nums" /></td>
                <td className="px-3 py-2"><input type="number" step="0.1" value={b.percentage ?? 0}
                  onChange={(e) => updateBucket(i, { percentage: Number(e.target.value) })}
                  className="w-24 border border-zinc-200 px-2 py-1 rounded-sm text-sm bg-white tabular-nums" /></td>
                <td className="px-3 py-2">
                  <button onClick={() => removeBucket(i)} className="text-xs text-red-600 hover:text-red-800">Remove</button>
                </td>
              </tr>
            ))}
            {(cfg.buckets || []).length === 0 && (
              <tr><td colSpan={5} className="text-center text-zinc-400 py-4 text-sm">No buckets yet — click "Add bucket".</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}


// ─────────────────────────────────────────────────────────────
// Quarter scheduler — Q1–Q4 start/close dates + auto-open toggle.
// BU Owners get a manual override on their own BU page (future).
// ─────────────────────────────────────────────────────────────
function QuarterScheduleSection({ s, set }) {
  const sched = s.quarter_schedule || {
    auto_open_close: true,
    quarters: ["Q1", "Q2", "Q3", "Q4"].map((q) => ({ quarter: q, starts_on: null, closes_on: null })),
  };
  const setSched = (patch) => set("quarter_schedule", { ...sched, ...patch });
  const updateQuarter = (i, patch) => {
    const next = (sched.quarters || []).slice();
    next[i] = { ...next[i], ...patch };
    setSched({ quarters: next });
  };

  return (
    <section data-testid="settings-schedule-section" className="max-w-3xl">
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Quarter scheduler</div>
        <h2 className="text-2xl font-semibold mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Automated open / close dates</h2>
        <p className="text-xs text-zinc-500 mt-1">
          When auto-scheduling is on, the system opens each quarter's KPA review window on <b>starts on</b> and closes
          it on <b>closes on</b>. BU Owners can still override manually from their BU dashboard.
        </p>
      </div>

      <label className="flex items-center gap-2 text-sm mb-4">
        <input type="checkbox" checked={!!sched.auto_open_close}
          data-testid="sched-auto"
          onChange={(e) => setSched({ auto_open_close: e.target.checked })} />
        Automatically open / close review windows on schedule
      </label>

      <div className="border border-zinc-200 rounded-sm bg-white overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-zinc-50">
            <tr>
              {["Quarter", "Starts on", "Closes on"].map((h) => (
                <th key={h} className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(sched.quarters || []).map((q, i) => (
              <tr key={q.quarter} className="border-t border-zinc-100" data-testid={`sched-row-${q.quarter}`}>
                <td className="px-3 py-2 font-semibold">{q.quarter}</td>
                <td className="px-3 py-2">
                  <input type="date" value={q.starts_on || ""}
                    onChange={(e) => updateQuarter(i, { starts_on: e.target.value || null })}
                    data-testid={`sched-${q.quarter}-start`}
                    className="border border-zinc-300 px-2 py-1 rounded-sm text-sm bg-white" />
                </td>
                <td className="px-3 py-2">
                  <input type="date" value={q.closes_on || ""}
                    onChange={(e) => updateQuarter(i, { closes_on: e.target.value || null })}
                    data-testid={`sched-${q.quarter}-close`}
                    className="border border-zinc-300 px-2 py-1 rounded-sm text-sm bg-white" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

